import { EN_GB, maybeT } from "./helpers";

function pad(n: number) {
    return n.toString(10).padStart(2, "0");
}

export function toISOStringSeconds(date: Date, localtime = false): string {
    if (localtime) {
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
    // remove milliseconds as its not supported by API
    return date.toISOString().replace(/\.\d{3}/gi, "");
}

export type DayOfWeek = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";
export const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;
export const TODAY = 'today';
export const YESTERDAY = 'yesterday';
export interface DateRangeConfig {
    // day of the week that weeks begin by
    weeksStartOn: DayOfWeek;
    // time of the day the days start at
    daysStartAt: number;
    // yield local time instead of UTC time
    useLocalTime?: boolean;
}

const PRESET_PERIODS = new Set([
    'today',
    'yesterday',
    'dayBeforeYesterday',
    'thisWeek',
    'lastWeek',
    'weekBeforeLastWeek',
    'thisMonth',
    'lastMonth',
    'monthBeforeLastMonth',
]);

export const isPresetPeriod = (period: string) => PRESET_PERIODS.has(period);

export const TIMEZONES = [
    {
        label: 'Dublin, Edinburgh, Lisbon, London (UTC+0)',
        value: 'Europe/London',
    },
    {
        label: 'Madrid, Paris, Berlin, Rome (UTC+1)',
        value: 'Europe/Madrid',
    },
    {
        label: 'Athens, Bucharest, Helsinki (UTC+2)',
        value: 'Europe/Athens',
    },
    {
        label: 'Moscow, Istanbul, Riyadh (UTC+3)',
        value: 'Europe/Moscow',
    },
    {
        label: 'São Paulo, Buenos Aires, Montevideo (UTC-3)',
        value: 'America/Sao_Paulo',
    },
    {
        label: 'Santiago, La Paz, Caracas (UTC-4)',
        value: 'America/Santiago',
    },
    {
        label: 'New York, Toronto, Havana (UTC-5)',
        value: 'America/New_York',
    },
    {
        label: 'Chicago, Dallas, Mexico City (UTC-6)',
        value: 'America/Chicago',
    },
    {
        label: 'Denver, Calgary, Phoenix (UTC-7)',
        value: 'America/Denver',
    },
    {
        label: 'Los Angeles, Vancouver, Tijuana (UTC-8)',
        value: 'America/Los_Angeles',
    },
    {
        label: 'Singapore, Kuala Lumpur, Beijing (UTC+8)',
        value: 'Asia/Singapore',
    },
    {
        label: 'Tokyo, Seoul, Osaka (UTC+9)',
        value: 'Asia/Tokyo',
    },
    {
        label: 'Sydney, Melbourne, Brisbane (UTC+10)',
        value: 'Australia/Sydney',
    },
];


export function startOfDay(date: Date, config?: DateRangeConfig): Date {
    const dat = new Date(date);
    if (config && dat.getHours() < config.daysStartAt && config.daysStartAt < 12) {
        dat.setDate(dat.getDate() - 1);
    }
    dat.setHours(config?.daysStartAt || 0);
    dat.setMinutes(0);
    dat.setSeconds(0);
    dat.setMilliseconds(0);
    return dat;
}

export function endOfDay(date: Date, config?: DateRangeConfig): Date {
    const dat = new Date(date);
    const daysStartAt = config ? config?.daysStartAt : 5;
    if (config && dat.getHours() < daysStartAt && config.daysStartAt < 12) {
        dat.setDate(dat.getDate() - 1);
    }
    dat.setHours((daysStartAt || 0) + 24);
    dat.setMinutes(0);
    dat.setSeconds(0);
    dat.setMilliseconds(0);
    return dat;
}

export function getTradingDate(date: Date, config?: DateRangeConfig): Date {
    const day = config?.daysStartAt && date.getHours() < config.daysStartAt ? date.getDate() - 1 : date.getDate();
    const dat = new Date(date.getFullYear(), date.getMonth(), day);
    dat.setHours(config?.daysStartAt || 0, 0, 0, 0);
    return dat;
}

function weekOffset(config?: DateRangeConfig): number {
    return ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].indexOf(config?.weeksStartOn || "monday");
}

export function dateRange(period: string, customFrom?: string, customTo?: string, forDate?: string, config?: DateRangeConfig): { startDate: string, endDate: string } {
    let startDate: Date = getTradingDate(forDate ? new Date(forDate) : new Date(), config);
    let endDate: Date = getTradingDate(forDate ? new Date(forDate) : new Date(), config);
    if (period === "today") {
        startDate = startOfDay(startDate, config);
        endDate = endOfDay(endDate, config);
    }
    if (period === "yesterday") {
        startDate = startOfDay(startDate, config);
        startDate.setDate(startDate.getDate() - 1);
        endDate = endOfDay(endDate, config);
        endDate.setDate(endDate.getDate() - 1);
    }
    if (period === "dayBeforeYesterday") {
        startDate = startOfDay(startDate, config);
        startDate.setDate(startDate.getDate() - 2);
        endDate = endOfDay(endDate, config);
        endDate.setDate(endDate.getDate() - 2);
    }
    if (period === "thisWeek") {
        const isOver = startDate.getDay() < weekOffset(config);
        startDate.setDate(startDate.getDate() - startDate.getDay() + weekOffset(config) - (isOver ? 7 : 0));
        endDate.setDate(endDate.getDate() - endDate.getDay() + 7 + weekOffset(config) - (isOver ? 7 : 0));
    }
    if (period === "lastWeek") {
        const isOver = startDate.getDay() < weekOffset(config);
        startDate.setDate(startDate.getDate() - startDate.getDay() - 7 + weekOffset(config) - (isOver ? 7 : 0));
        endDate.setDate(endDate.getDate() - endDate.getDay() + weekOffset(config) - (isOver ? 7 : 0));
    }
    if (period === "weekBeforeLastWeek") {
        const isOver = startDate.getDay() < weekOffset(config);
        startDate.setDate(startDate.getDate() - startDate.getDay() - 14 + weekOffset(config) - (isOver ? 7 : 0));
        endDate.setDate(endDate.getDate() - endDate.getDay() - 7 + weekOffset(config) - (isOver ? 7 : 0));
    }
    if (period === "thisMonth") {
        startDate.setDate(1);
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1, config?.daysStartAt);
    }
    if (period === "lastMonth") {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1, config?.daysStartAt);
        endDate.setDate(1);
    }
    if (period === "monthBeforeLastMonth") {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth() - 2, 1, config?.daysStartAt);
        endDate.setDate(1);
    }
    if (period === "custom") {
        if (!customFrom || !customTo) {
            throw new Error("a custom date range was defined but no custom dates were supplied");
        }
        startDate = new Date(customFrom);
        endDate = new Date(customTo);
    }
    return {
        startDate: toISOStringSeconds(startDate, config?.useLocalTime),
        endDate: toISOStringSeconds(endDate, config?.useLocalTime)
    };
}

export function getLabelForPeriod(period: string, customFrom?: string, customTo?: string, translate?: (key: string) => string, locale = EN_GB): string {
    switch (period) {
        case "today":
            return maybeT(translate, "components.datePicker.today", "Today");
        case "yesterday":
            return maybeT(translate, "components.datePicker.yesterday", "Yesterday");
        case "thisWeek":
            return maybeT(translate, "components.datePicker.thisWeek", "This Week");
        case "lastWeek":
            return maybeT(translate, "components.datePicker.lastWeek", "Last Week");
        case "thisMonth":
            return maybeT(translate, "components.datePicker.thisMonth", "This Month");
        case "lastMonth":
            return maybeT(translate, "components.datePicker.lastMonth", "Last Month");
        case "custom":
            if (customFrom && customTo) {
                const from = new Date(customFrom);
                const to = new Date(customTo);
                return `${maybeT(translate, "components.datePicker.from", "From")} ${from.toLocaleDateString(locale)} ${maybeT(translate, "components.datePicker.to", "to")} ${to.toLocaleDateString(locale)}`;
            }
            return "Custom";
        default:
            if (customFrom && customTo) {
                const from = new Date(customFrom);
                const to = new Date(customTo);
                return `${maybeT(translate, "components.datePicker.from", "From")} ${from.toLocaleDateString(locale)} ${maybeT(translate, "components.datePicker.to", "to")} ${to.toLocaleDateString(locale)}`;
            }
            return "Unknown";
    }

}

const areSameDay = (first: Date, second: Date) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();

export const isToday = (date: Date) => areSameDay(date, new Date());

export const isYesterday = (date: Date) => areSameDay(date, new Date(Date.now() - MILLIS_PER_DAY));

export function formatDate(inputDate: string, locale = EN_GB, settings: Intl.DateTimeFormatOptions = { dateStyle: 'short', timeStyle: 'short' }): string {
    return Intl.DateTimeFormat(locale, settings).format(
        Date.parse(inputDate)
    );
}

export function TInputDateToString(date: Date | string | null | undefined, config?: DateRangeConfig) {
    return date ? toISOStringSeconds(new Date(date), config?.useLocalTime || true) : "";
}

export const formatTime = (date: string, locale = EN_GB) => {
    const parsedDate = Date.parse(date);
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(parsedDate);
}

export const formatDateTimeParam = (date: string) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
};

export const formatDateParam = (date: string) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

export const formatPeriodLabel = (date: Date, locale = EN_GB) =>{
  try {
    const supportedLocale = Intl.DateTimeFormat.supportedLocalesOf([locale])[0] || EN_GB;

    return new Intl.DateTimeFormat(supportedLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(EN_GB, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }
};

const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1)

const isPreviousYear = (date: Date) => date.getFullYear() < new Date().getFullYear();


export const getDateHeaderData = (
  dateAsString: string,
  translate?: (key: string) => string,
  locale = "en-GB"
) => {
  const date = new Date(dateAsString);

  let dayName = capitalizeFirstLetter(
    date.toLocaleDateString(locale, { weekday: "long" })
  );

  if (isToday(date)) {
    dayName = maybeT(translate, "components.datePicker.today", "Today");
  } else if (isYesterday(date)) {
    dayName = maybeT(translate, "components.datePicker.yesterday", "Yesterday");
  }

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    ...(isPreviousYear(date) ? { year: "numeric" } : {}),
  });

  const formatted = dateFormatter.format(date);

  return `${dayName}, ${formatted}`;
};

export const isDateWithinRange = (
    targetDate: Date | string,
    startDate?: Date | string,
    endDate?: Date | string
): boolean => {
    const target = new Date(targetDate).getTime();

    const afterStart =
        !startDate || target >= new Date(startDate).getTime();

    const beforeEnd =
        !endDate || target <= new Date(endDate).getTime();

    return afterStart && beforeEnd;
};
